import { BaseComponent, BElement } from "utils/base-component"
import throttle from "utils/throttle"
import { Dot } from "./dot"

import "./index.scss"

type CarouselItem = {
  element: HTMLElement
  index: number
  width: number
  left: number
  isVisible?: boolean
}

const VISIBLE_RATIO = 0.2

const isItemVisible = (item: CarouselItem, viewportStart: number, viewportEnd: number) => {
  if (!document.body.contains(item.element)) return false
  if (item.width === 0) return false

  const minVisiblePosition = item.left + item.width * VISIBLE_RATIO
  const maxVisiblePosition = item.left + item.width * (1 - VISIBLE_RATIO)

  return minVisiblePosition >= viewportStart && maxVisiblePosition <= viewportEnd
}

export class Carousel extends BaseComponent {
  static cssClass = "carousel"

  private dotsContainer?: BElement
  private dots!: Dot[]
  private itemsList?: HTMLElement
  private leftButton?: BElement
  private rightButton?: BElement
  private items!: CarouselItem[]
  private currentItems!: CarouselItem[]
  private autoTimeout?: number

  init() {
    this.items = []
    this.currentItems = []
    this.dots = []

    const listElement = this.element("list")
    if (!listElement) return
    this.itemsList = listElement.element

    this.dotsContainer = this.element("dots") ?? undefined

    const arrows = this.elements("arrow")
    this.leftButton = arrows.find((arrow) => arrow.hasModifier("left"))
    this.rightButton = arrows.find((arrow) => arrow.hasModifier("right"))

    this.updateItems()

    if (this.dotsContainer) {
      this.renderDots()
    }

    this.itemsList.addEventListener(
      "scroll",
      throttle(() => {
        this.update()
        this.auto()
      }, 50),
    )

    window.addEventListener(
      "resize",
      throttle(() => {
        this.updateItems()
        this.update()
        this.auto()
      }, 1000),
    )

    if (this.leftButton) {
      this.leftButton.element.addEventListener("click", (event) => {
        event.preventDefault()
        this.move("left")
      })
    }

    if (this.rightButton) {
      this.rightButton.element.addEventListener("click", (event) => {
        event.preventDefault()
        this.move("right")
      })
    }

    this.update()
    this.auto()
  }

  private auto() {
    if (this.autoTimeout) window.clearTimeout(this.autoTimeout)

    this.autoTimeout = window.setTimeout(() => {
      this.autoTimeout = undefined
      this.move("right")
      this.auto()
    }, 10000)
  }

  move(direction: "left" | "right") {
    if (!this.itemsList) return
    if (this.items.length === 0) return
    if (this.currentItems.length === 0) return

    let nextPosition = 0
    const firstVisible = this.currentItems[0]

    if (direction === "left") {
      if (this.items[0].isVisible) {
        nextPosition = this.items[this.items.length - 1].left ?? 0
      } else {
        const beforeFirstVisible = this.items[firstVisible.index - 1]
        nextPosition = beforeFirstVisible ? beforeFirstVisible.left : nextPosition
      }
    }

    if (direction === "right") {
      if (this.items[this.items.length - 1].isVisible) {
        nextPosition = this.items[0].left ?? 0
      } else {
        const afterFirstVisible = this.items[firstVisible.index + 1]
        nextPosition = afterFirstVisible ? afterFirstVisible.left : nextPosition
      }
    }

    this.itemsList.scrollLeft = nextPosition
  }

  private moveToIndex(index: number) {
    const item = this.items[index]
    if (item) {
      this.moveTo(item)
    }
  }

  private moveTo(item: CarouselItem) {
    if (!this.itemsList) return
    this.itemsList.scrollLeft = item.left
  }

  private update() {
    this.checkItemsVisibility()
    this.currentItems = this.items.filter((item) => item.isVisible)

    if (this.dots.length) {
      this.items.forEach((item, index) => {
        this.dots[index]?.setVisible(Boolean(item.isVisible))
      })
    }

    const allVisible = this.currentItems.length === this.items.length
    this.toggleControls(allVisible)
  }

  private toggleControls(allVisible: boolean) {
    if (this.leftButton && this.rightButton) {
      this.leftButton.toggleModifier("hidden", allVisible)
      this.rightButton.toggleModifier("hidden", allVisible)
    }

    if (this.dotsContainer) {
      this.dotsContainer.toggleModifier("hidden", allVisible)
    }
  }

  private updateItems() {
    if (!this.itemsList) return

    const itemsListBox = this.itemsList.getBoundingClientRect()
    this.items = Array.from(this.itemsList.children).map((child, index) => {
      const element = child as HTMLElement
      const itemBox = element.getBoundingClientRect()
      return {
        element,
        index,
        width: itemBox.width,
        left: Math.abs(itemBox.left - itemsListBox.left),
      }
    })
  }

  private checkItemsVisibility() {
    if (!this.itemsList) return
    const listWidth = this.itemsList.getBoundingClientRect().width
    const viewportStart = this.itemsList.scrollLeft
    const viewportEnd = viewportStart + listWidth

    this.items = this.items.map((item) => ({
      ...item,
      isVisible: isItemVisible(item, viewportStart, viewportEnd),
    }))
  }

  private renderDots() {
    if (!this.dotsContainer) return
    this.dotsContainer.element.innerHTML = ""

    this.dots = this.items.map((item, index) => {
      const dot = new Dot(index, this.cssClass, () => this.moveToIndex(index))
      dot.setVisible(Boolean(item.isVisible))
      this.dotsContainer!.element.appendChild(dot.root)
      return dot
    })
  }
}
